import { useState, useRef, useEffect } from 'react';
import { useOnClickOutside } from 'src/hooks/useOneClickOutsideHook';
import { useLabels } from 'src/StoreDataProvider';
import { DropdownProps } from './dropdown.types';
import ListElement from './ListElement';

const Dropdown: (props: DropdownProps) => JSX.Element = ({
  id,
  label,
  selected,
  items,
  onChange,
  multiple = false,
}) => {
  const labels = useLabels();
  const [open, setOpen] = useState<boolean>(false);
  const [flexibleConnect] = useState<boolean>(false);
  const [dropDownString, setDropDownString] = useState<string>('');
  const [isInitial, setIsInitial] = useState<boolean>(true);
  const dropdownRef = useRef<HTMLInputElement>(null);
  const [childFoccused, setChildFoccused] = useState<boolean>(false);

  useEffect(() => {
    const selectedItemsLabel: string[] = [];
    selected.forEach(key => {
      const selectedItem =
        items.length > 0 &&
        items.find(item => {
          return item.key === key;
        });
      if (selectedItem) {
        selectedItemsLabel.push(selectedItem.value);
      }
    });
    setDropDownString(
      selectedItemsLabel.length > 0
        ? selectedItemsLabel.join(', ')
        : items.length < 1
          ? labels.dropdownOptionLoadingFailure
          : labels.select
    );
  }, [selected, items]);

  useEffect(() => {
    if (!open && !isInitial && dropdownRef.current) {
      dropdownRef.current.focus();
    } else if (isInitial) {
      setIsInitial(false);
    }
  }, [open]);

  const getFieldId = (praefix: string, appendix?: string): string => {
    let tempAppendix = '';
    if (appendix) {
      tempAppendix = `_${appendix}`;
    }
    return id ? `${praefix}_${id}${tempAppendix}` : `${praefix}_not_found`;
  };

  const dropDownRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(dropDownRef, () => setOpen(false));
  return (
    <div className={`dropdown${open ? ' open' : ''}`}>
      <label
        htmlFor={getFieldId('dropdown_input')}
        className="dropdown__label"
        id={getFieldId('dropdown_label')}
      >
        <span className="dropdown__label__value">{label}</span>
      </label>
      <div className="dropdown__wrapper" ref={dropDownRef}>
        <div
          aria-controls={getFieldId('dropdown_ul')}
          aria-expanded={open}
          aria-haspopup="listbox"
          role="combobox"
          tabIndex={0}
          className={
            `dropdown__field${multiple ? ' dropdown__field__multiple' : ''}${childFoccused ? ' dropdown__field__focusLook' : ''}`
          }
          id={getFieldId('dropdown_field')}
          ref={dropdownRef}
          onClick={() => {
            setOpen(true);
          }}
          onKeyDown={e => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter') setOpen(true);
            if (e.key === ' ' || e.key === 'Spacebar') {
              e.preventDefault();
              setOpen(true);
            }
            if (e.key === 'ArrowDown') {
              setOpen(true);
              document.getElementById(`dropdown__${id}_${0}`)?.focus();
              e.preventDefault();
            }
            if (e.key === 'Tab') setOpen(false);
          }}
        >
          {selected.length > 0 && multiple && (
            <span className="dropdown__value-lenght"> {selected.length} </span>
          )}
          <span className="dropdown__text">{dropDownString}</span>
          <em id={getFieldId('expand')} className="icon-pfeil-runter" />
        </div>
        <ul
          id={getFieldId('dropdown_ul')}
          role="listbox"
          aria-labelledby={getFieldId('dropdown_label')}
          tabIndex={-1}
          data-cy={`options_${id}`}
          className={`options${flexibleConnect ? ' option__connect-bottom' : ''
            }`}
        >
          <ListElement
            id={`dropdown__${id}`}
            items={items}
            selected={selected}
            onChange={(selected, e): void => {
              if (!multiple) {
                setOpen(false);
              }
              onChange(selected);
              e.stopPropagation();
            }}
            onFocus={() =>
              setChildFoccused(true)
            }
            onBlur={() =>
              setChildFoccused(false)
            }
            multiple={multiple}
            setOpen={setOpen}
          />
        </ul>
      </div>
    </div>
  );
};

export default Dropdown;
